import { useState } from 'react';

type SpaceType = 'residential' | 'office' | 'retail' | 'hospitality' | 'mixed-use';
type OptimizationGoal = 'maximize-space' | 'natural-light' | 'circulation' | 'privacy' | 'collaboration' | 'accessibility';

interface SpaceUnit {
  id: string;
  name: string;
  area: number;
  color: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

const SPACE_TYPES: { id: SpaceType; label: string; icon: string; desc: string }[] = [
  { id: 'residential', label: 'Residential', icon: 'ri-home-4-line', desc: 'Apartments, condos, single-family homes' },
  { id: 'office', label: 'Office', icon: 'ri-building-line', desc: 'Open plans, private offices, co-working' },
  { id: 'retail', label: 'Retail', icon: 'ri-store-2-line', desc: 'Shops, showrooms, boutiques' },
  { id: 'hospitality', label: 'Hospitality', icon: 'ri-hotel-line', desc: 'Hotels, restaurants, cafes' },
  { id: 'mixed-use', label: 'Mixed-Use', icon: 'ri-community-line', desc: 'Combined residential & commercial' },
];

const OPTIMIZATION_GOALS: { id: OptimizationGoal; label: string; icon: string }[] = [
  { id: 'maximize-space', label: 'Maximize Space', icon: 'ri-expand-diagonal-line' },
  { id: 'natural-light', label: 'Natural Light', icon: 'ri-sun-line' },
  { id: 'circulation', label: 'Flow & Circulation', icon: 'ri-route-line' },
  { id: 'privacy', label: 'Privacy Zones', icon: 'ri-shield-line' },
  { id: 'collaboration', label: 'Collaboration', icon: 'ri-group-line' },
  { id: 'accessibility', label: 'Accessibility', icon: 'ri-wheelchair-line' },
];

const SPACE_UNITS: SpaceUnit[] = [
  { id: 'living', name: 'Living Room', area: 28, color: '#14B8A6', x: 0, y: 0, w: 3, h: 2 },
  { id: 'kitchen', name: 'Kitchen', area: 16, color: '#F59E0B', x: 3, y: 0, w: 2, h: 2 },
  { id: 'master-bed', name: 'Master Bed', area: 22, color: '#8B5CF6', x: 0, y: 2, w: 2, h: 2 },
  { id: 'bed2', name: 'Bedroom 2', area: 16, color: '#EC4899', x: 2, y: 2, w: 2, h: 2 },
  { id: 'bath', name: 'Bathroom', area: 8, color: '#06B6D4', x: 4, y: 2, w: 1, h: 1 },
  { id: 'hall', name: 'Hallway', area: 6, color: '#6B7280', x: 4, y: 3, w: 1, h: 1 },
];

const ANALYSIS_METRICS = [
  { label: 'Space Efficiency', value: 87, color: 'teal', icon: 'ri-bar-chart-line' },
  { label: 'Natural Light Score', value: 74, color: 'amber', icon: 'ri-sun-line' },
  { label: 'Circulation Flow', value: 91, color: 'cyan', icon: 'ri-route-line' },
  { label: 'Privacy Index', value: 68, color: 'violet', icon: 'ri-shield-line' },
];

const QBIQ_SUGGESTIONS = [
  { icon: 'ri-arrow-right-circle-line', text: 'Rotate master bedroom 90° to improve natural light by ~18%', priority: 'high' },
  { icon: 'ri-layout-line', text: 'Merge hallway with living room for 12% more usable area', priority: 'medium' },
  { icon: 'ri-door-open-line', text: 'Add sliding door between kitchen and living for better flow', priority: 'medium' },
  { icon: 'ri-window-line', text: 'Add east-facing window to Bedroom 2 for morning light', priority: 'low' },
];

interface SpacePlannerProps {
  darkMode: boolean;
}

export default function SpacePlanner({ darkMode }: SpacePlannerProps) {
  const [spaceType, setSpaceType] = useState<SpaceType>('residential');
  const [goals, setGoals] = useState<OptimizationGoal[]>(['maximize-space', 'natural-light']);
  const [totalArea, setTotalArea] = useState(120);
  const [floors, setFloors] = useState(1);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [activeUnit, setActiveUnit] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [exportToast, setExportToast] = useState(false);

  const toggleGoal = (id: OptimizationGoal) => {
    setGoals(prev => prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]);
  };

  const handleAnalyze = () => {
    setIsAnalyzing(true);
    setAnalysisComplete(false);
    setAnalysisProgress(0);
    let prog = 0;
    const interval = setInterval(() => {
      prog += Math.random() * 20 + 10;
      if (prog >= 100) {
        prog = 100;
        clearInterval(interval);
        setIsAnalyzing(false);
        setAnalysisComplete(true);
        setShowSuggestions(true);
      }
      setAnalysisProgress(Math.round(prog));
    }, 300);
  };

  const colorMap: Record<string, string> = {
    teal: 'text-teal-400',
    amber: 'text-amber-400',
    cyan: 'text-cyan-400',
    violet: 'text-violet-400',
  };

  const bgColorMap: Record<string, string> = {
    teal: 'bg-teal-400',
    amber: 'bg-amber-400',
    cyan: 'bg-cyan-400',
    violet: 'bg-violet-400',
  };

  return (
    <div className="space-y-5">
      <div className="mb-2">
        <h3 className="text-base font-bold text-gray-900 mb-1">AI Space Planner</h3>
        <p className="text-xs text-gray-400">Powered by Qbiq AI — intelligent space optimization &amp; layout analysis</p>
      </div>

      {/* Space Type */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h4 className="text-xs font-semibold text-teal-600 uppercase tracking-wider mb-3">Space Type</h4>
        <div className="space-y-2">
          {SPACE_TYPES.map(type => (
            <button
              key={type.id}
              onClick={() => { setSpaceType(type.id); setAnalysisComplete(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition-all cursor-pointer text-left ${
                spaceType === type.id ? 'bg-teal-50 border border-teal-300 text-teal-700' : 'bg-gray-50 border border-gray-200 text-gray-600 hover:border-teal-200'
              }`}
            >
              <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                <i className={`${type.icon} text-base`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold whitespace-nowrap">{type.label}</p>
                <p className="text-gray-400 text-xs">{type.desc}</p>
              </div>
              {spaceType === type.id && (
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-teal-500 text-sm"></i>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Area & Floors */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
        <h4 className="text-xs font-semibold text-teal-600 uppercase tracking-wider">Dimensions</h4>
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-gray-500 font-medium">Total Area</p>
            <span className="text-xs text-teal-600 font-semibold">{totalArea} m²</span>
          </div>
          <input
            type="range"
            min={30}
            max={500}
            step={10}
            value={totalArea}
            onChange={e => { setTotalArea(Number(e.target.value)); setAnalysisComplete(false); }}
            className="w-full accent-teal-500"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>30m²</span><span>500m²</span>
          </div>
        </div>
        <div>
          <p className="text-xs text-gray-500 font-medium mb-2">Number of Floors</p>
          <div className="flex gap-2">
            {[1, 2, 3, 4].map(f => (
              <button
                key={f}
                onClick={() => { setFloors(f); setAnalysisComplete(false); }}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  floors === f ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Optimization Goals */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h4 className="text-xs font-semibold text-teal-600 uppercase tracking-wider mb-3">Optimization Goals</h4>
        <div className="grid grid-cols-2 gap-2">
          {OPTIMIZATION_GOALS.map(goal => (
            <button
              key={goal.id}
              onClick={() => { toggleGoal(goal.id); setAnalysisComplete(false); }}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-all cursor-pointer text-left ${
                goals.includes(goal.id) ? 'bg-teal-50 border border-teal-400 text-teal-700' : 'bg-gray-50 border border-gray-200 text-gray-600 hover:border-teal-200'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className={`${goal.icon} text-sm`}></i>
              </div>
              <span className="whitespace-nowrap">{goal.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Analyze Button */}
      <button
        onClick={handleAnalyze}
        disabled={isAnalyzing || goals.length === 0}
        className={`w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all cursor-pointer whitespace-nowrap ${
          isAnalyzing || goals.length === 0
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white hover:shadow-lg hover:shadow-cyan-500/30'
        }`}
      >
        {isAnalyzing ? (
          <><i className="ri-loader-4-line animate-spin"></i> Analyzing Space... {analysisProgress}%</>
        ) : (
          <><i className="ri-scan-line"></i> Analyze &amp; Optimize Space</>
        )}
      </button>

      {isAnalyzing && (
        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-cyan-500 to-teal-500 transition-all duration-300"
            style={{ width: `${analysisProgress}%` }}
          ></div>
        </div>
      )}

      {/* Analysis Results */}
      {analysisComplete && (
        <div className="space-y-4">
          {/* Visual Floor Plan Grid */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900">Optimized Layout</h4>
              <span className="text-xs text-teal-600 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-full">Qbiq AI</span>
            </div>
            <div
              className="grid gap-1 rounded-lg overflow-hidden"
              style={{ gridTemplateColumns: 'repeat(5, 1fr)', gridTemplateRows: 'repeat(4, 60px)' }}
            >
              {SPACE_UNITS.map(unit => (
                <button
                  key={unit.id}
                  onClick={() => setActiveUnit(activeUnit === unit.id ? null : unit.id)}
                  className={`flex flex-col items-center justify-center rounded-md transition-all cursor-pointer ${
                    activeUnit === unit.id ? 'ring-2 ring-gray-800 scale-95' : 'hover:brightness-95'
                  }`}
                  style={{
                    backgroundColor: unit.color + '22',
                    border: `2px solid ${unit.color}55`,
                    gridColumn: `span ${unit.w}`,
                    gridRow: `span ${unit.h}`,
                  }}
                >
                  <span className="text-gray-800 text-xs font-semibold text-center leading-tight px-1">{unit.name}</span>
                  <span className="text-gray-500 text-xs">{unit.area}m²</span>
                </button>
              ))}
            </div>
            {activeUnit && (
              <div className="mt-3 p-3 bg-teal-50 rounded-lg border border-teal-200">
                <p className="text-xs text-teal-700 font-semibold">{SPACE_UNITS.find(u => u.id === activeUnit)?.name}</p>
                <p className="text-xs text-gray-500 mt-1">Area: {SPACE_UNITS.find(u => u.id === activeUnit)?.area}m² · Click to deselect</p>
              </div>
            )}
          </div>

          {/* Metrics */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Space Analysis Scores</h4>
            <div className="space-y-3">
              {ANALYSIS_METRICS.map(metric => (
                <div key={metric.label}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${metric.icon} text-sm ${colorMap[metric.color]}`}></i>
                      </div>
                      <span className="text-xs text-gray-700 font-medium">{metric.label}</span>
                    </div>
                    <span className={`text-xs font-bold ${colorMap[metric.color]}`}>{metric.value}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${bgColorMap[metric.color]} rounded-full transition-all duration-700`}
                      style={{ width: `${metric.value}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Suggestions */}
          {showSuggestions && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-lightbulb-line text-amber-500"></i>
                </div>
                <h4 className="text-sm font-semibold text-gray-900">Qbiq AI Suggestions</h4>
              </div>
              <div className="space-y-2">
                {QBIQ_SUGGESTIONS.map((s, i) => (
                  <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${
                    s.priority === 'high' ? 'bg-amber-50 border-amber-200' :
                    s.priority === 'medium' ? 'bg-teal-50 border-teal-200' :
                    'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${s.icon} text-sm ${
                        s.priority === 'high' ? 'text-amber-500' :
                        s.priority === 'medium' ? 'text-teal-500' : 'text-gray-400'
                      }`}></i>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-700 leading-relaxed">{s.text}</p>
                      <span className={`text-xs font-medium mt-1 inline-block ${
                        s.priority === 'high' ? 'text-amber-600' :
                        s.priority === 'medium' ? 'text-teal-600' : 'text-gray-400'
                      }`}>
                        {s.priority.charAt(0).toUpperCase() + s.priority.slice(1)} priority
                      </span>
                    </div>
                    <button className="text-xs text-teal-500 hover:text-teal-600 cursor-pointer whitespace-nowrap flex-shrink-0 font-medium">
                      Apply
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Export */}
          <div className="flex gap-3">
            <button
              onClick={() => { setExportToast(true); setTimeout(() => setExportToast(false), 3000); }}
              className="flex-1 py-2.5 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap hover:shadow-lg hover:shadow-cyan-500/30 transition-all"
            >
              <i className="ri-download-line"></i> Export Layout
            </button>
            <button
              onClick={() => { setAnalysisComplete(false); setShowSuggestions(false); }}
              className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap transition-all"
            >
              <i className="ri-refresh-line"></i> Re-Analyze
            </button>
          </div>
        </div>
      )}

      {exportToast && (
        <div className="fixed bottom-6 right-6 px-4 py-3 bg-gray-900 text-white rounded-lg shadow-lg flex items-center gap-2 z-50">
          <div className="w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center flex-shrink-0">
            <i className="ri-check-line text-xs"></i>
          </div>
          <span className="text-sm font-medium">Layout exported successfully!</span>
        </div>
      )}
    </div>
  );
}
